
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Card,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

interface InstructionPopupProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  instructions: string[];
}

const InstructionPopup: React.FC<InstructionPopupProps> = ({
  open,
  onClose,
  onContinue,
  instructions,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#2d3b5e',
          borderRadius: 3,
          p: 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '30px 20px 20px 20px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MicIcon sx={{ color: '#fff', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500 }}>
            Check voice recording instructions
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />

      <DialogContent sx={{ padding: '10px 40px', mt: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {/* Left side - Illustration */}
          <Card
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 45%' },
              bgcolor: '#1a1a1a',
              border: 'none',
              borderRadius: 2,
              p: 0,
              display: 'flex',
            //   minHeight: 350,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <img src='/assets/MicImage.svg' alt='Microphone Illustration' style={{ width: '100%', objectFit: 'cover', borderRadius: '10px' }} />
          </Card>

          {/* Right side - Instructions */}
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 45%' },
              borderRadius: 2,
              p: 0,
              border: '1px solid #4D518F',
              height: 'max-content'
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: '#fff', fontWeight: 500, bgcolor: '#393D69', p: 1 }}
            >
              Follow these instructions:
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {instructions.map((inst, idx) => (
                <Box key={idx}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', padding: '10px 20px' }}>
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: 18,
                        fontWeight: 600,
                        minWidth: 40,
                      }}
                    >
                      #{idx + 1}
                    </Typography>
                    {/* Special icon for record instruction */}
                    {inst.toLowerCase().includes('record') ? (
                      <Typography sx={{ color: '#e0e0e0', fontSize: 16, lineHeight: 1.6 }}>
                        {inst.split('Record')[0]}
                        Click Record{' '}
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: '#ff4444',
                            mx: 0.5,
                          }}
                        >
                          <MicIcon sx={{ fontSize: 14, color: '#fff' }} />
                        </Box>{' '}
                        {inst.split('Record')[1]}
                      </Typography>
                    ) : (
                      <Typography sx={{ color: '#e0e0e0', fontSize: 16, lineHeight: 1.6 }}>
                        {inst}
                      </Typography>
                    )}
                  </Box>
                  {idx < instructions.length - 1 && (
                    <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ mt: 3, justifyContent: 'flex-end', mb: 3, padding: '10px 20px' }}>
        <Button
          onClick={onContinue}
          variant="contained"
          size="large"
          sx={{
            bgcolor: '#7c4dff',
            color: '#fff',
            padding: 4,
            py: 1.5,
            fontSize: 16,
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: 2,
            '&:hover': {
              bgcolor: '#6a3de8',
            },
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstructionPopup;
